import React, { useMemo, useState } from "react";
import { Column, SimpleTable } from "../../../Handbook/_components/ui/SimpleTable/SimpleTable";
import styles from "./ScheduleTable.module.css";
import type { ScheduleItem } from "./mockScheduleData";

interface ScheduleTableProps {
    title: string;
    data: ScheduleItem[];
    loading?: boolean;
    error?: string | null;
}

const PREVIEW_DATES_LIMIT = 3;

const parseDate = (value: string): Date => {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = new Date(value.slice(0, 10));
        if (!Number.isNaN(d.getTime())) return d;
    }
    const parts = value.split(".");
    if (parts.length === 3) {
        const [day, month, year] = parts.map(Number);
        return new Date(year, month - 1, day);
    }
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? new Date(0) : fallback;
};

const sortDates = (dates: string[]): string[] => {
    return [...dates].sort((a, b) => parseDate(a).getTime() - parseDate(b).getTime());
};

const rowKey = (row: ScheduleItem) =>
    row.id ? `${row.geofence}-${row.id}` : `${row.geofence}-${row.cpName}`;

export const ScheduleTable = ({
    title: _title,
    data,
    loading = false,
    error = null,
}: ScheduleTableProps) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [expandedGeofences, setExpandedGeofences] = useState<Record<string, boolean>>({});
    const [search, setSearch] = useState("");
    const [geofenceSearch, setGeofenceSearch] = useState("");

    const preparedData = useMemo<ScheduleItem[]>(
        () =>
            data.map((item) => ({
                ...item,
                plannedDates: sortDates(item.plannedDates),
            })),
        [data]
    );

    const filteredData = useMemo(
        () =>
            preparedData.filter((item) => {
                const cpMatch = item.cpName
                    .toLowerCase()
                    .includes(search.trim().toLowerCase());
                const geofenceMatch = item.geofence
                    .toLowerCase()
                    .includes(geofenceSearch.trim().toLowerCase());

                return cpMatch && geofenceMatch;
            }),
        [preparedData, search, geofenceSearch]
    );

    const groupedData = useMemo<Record<string, ScheduleItem[]>>(
        () =>
            filteredData.reduce<Record<string, ScheduleItem[]>>((acc, item) => {
                if (!acc[item.geofence]) {
                    acc[item.geofence] = [];
                }

                acc[item.geofence].push(item);
                return acc;
            }, {}),
        [filteredData]
    );

    const toggleExpanded = (rowKey: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowKey]: !prev[rowKey],
        }));
    };

    const toggleGeofence = (geofence: string) => {
        setExpandedGeofences((prev) => ({
            ...prev,
            [geofence]: !prev[geofence],
        }));
    };

    const handleExportExcelClick = () => {
        // Заглушка для будущего экспорта: точка входа уже подготовлена.
        console.log("Export schedule to Excel", filteredData);
    };

    const columns: Column<ScheduleItem>[] = [
        {
            key: "cpName",
            title: "Название КП",
            render: (row) => <div className={styles.nameCell}>{row.cpName}</div>,
        },
        {
            key: "datesCount",
            title: "Количество дат",
            render: (row) => (
                <div className={styles.countCell}>
                    <span className={styles.countBadge}>{row.plannedDates.length}</span>
                </div>
            ),
        },
        {
            key: "plannedDates",
            title: "Плановые даты",
            render: (row) => {
                if (!row.plannedDates.length) {
                    return <span className={styles.emptyDates}>Нет плановых дат</span>;
                }

                const rk = rowKey(row);
                const isExpanded = Boolean(expandedRows[rk]);
                const previewDates = row.plannedDates.slice(0, PREVIEW_DATES_LIMIT);
                const visibleDates = isExpanded ? row.plannedDates : previewDates;
                const hiddenCount = Math.max(0, row.plannedDates.length - PREVIEW_DATES_LIMIT);

                return (
                    <div className={styles.datesCell}>
                        <div className={styles.datesList}>
                            {visibleDates.map((date) => (
                                <span className={styles.chip} key={`${rk}-${date}`}>
                                    {date}
                                </span>
                            ))}
                            {hiddenCount > 0 && (
                                <button
                                    type="button"
                                    className={styles.expandBtn}
                                    onClick={() => toggleExpanded(rk)}
                                >
                                    {isExpanded ? "Скрыть" : `Еще +${hiddenCount}`}
                                </button>
                            )}
                        </div>
                    </div>
                );
            },
        },
    ];

    const groupedEntries = Object.entries(groupedData);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingState}>Загрузка расписания…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.errorState}>{error}</div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.searchRow}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Поиск по геозоне"
                    value={geofenceSearch}
                    onChange={(e) => setGeofenceSearch(e.target.value)}
                />
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Поиск по названию КП"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {!filteredData.length && (
                <div className={styles.emptyState}>Нет данных расписания</div>
            )}

            {groupedEntries.map(([geofence, items]) => (
                <div key={geofence} className={styles.groupBlock}>
                    <button
                        type="button"
                        className={styles.groupHeader}
                        onClick={() => toggleGeofence(geofence)}
                    >
                        <span className={styles.groupTitle}>{geofence}</span>
                        <span className={styles.groupMeta}>
                            {items.length} КП
                            <span className={styles.groupChevron}>
                                {expandedGeofences[geofence] ? "▾" : "▸"}
                            </span>
                        </span>
                    </button>

                    {expandedGeofences[geofence] && (
                        <SimpleTable<ScheduleItem>
                            title={geofence}
                            hideHeader
                            columns={columns}
                            data={items}
                            getRowKey={rowKey}
                            emptyText="Нет данных расписания"
                            isNeedAdd={false}
                            isNeedActions={false}
                        />
                    )}
                </div>
            ))}

            <div className={styles.exportWrap}>
                <button
                    type="button"
                    className={styles.exportButton}
                    onClick={handleExportExcelClick}
                >
                    Выгрузить в Excel
                </button>
            </div>
        </div>
    );
};
